import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Emotion Network Graph - Shows connections between emotions
 * Inspired by hodline.com interactive visualizations
 */
export const EmotionNetworkGraph = ({ analyses }) => {
  const svgRef = useRef(null);
  const [selectedEmotion, setSelectedEmotion] = useState(null);

  useEffect(() => {
    if (!analyses || analyses.length === 0 || !svgRef.current) return;

    // Extract emotion data
    const emotionData = {};
    const emotionConnections = [];

    analyses.forEach((analysis) => {
      const emotions = analysis.emotions || {};
      const dominant = analysis.dominant_emotion;

      Object.entries(emotions).forEach(([emotion, score]) => {
        if (!emotionData[emotion]) {
          emotionData[emotion] = {
            name: emotion,
            count: 0,
            totalScore: 0,
            avgScore: 0,
          };
        }
        emotionData[emotion].count += 1;
        emotionData[emotion].totalScore += score;
      });

      // Track co-occurring emotions
      const emotionList = Object.keys(emotions);
      for (let i = 0; i < emotionList.length; i++) {
        for (let j = i + 1; j < emotionList.length; j++) {
          emotionConnections.push({
            source: emotionList[i],
            target: emotionList[j],
            value: emotions[emotionList[i]] * emotions[emotionList[j]],
          });
        }
      }
    });

    // Calculate averages
    Object.values(emotionData).forEach((emotion) => {
      emotion.avgScore = emotion.totalScore / emotion.count;
    });

    // Prepare nodes and links
    const nodes = Object.values(emotionData).map((e) => ({
      id: e.name,
      value: e.count,
      avgScore: e.avgScore,
    }));

    // Aggregate connections
    const linkMap = {};
    emotionConnections.forEach((conn) => {
      const key = [conn.source, conn.target].sort().join('-');
      if (!linkMap[key]) {
        linkMap[key] = { source: conn.source, target: conn.target, value: 0 };
      }
      linkMap[key].value += conn.value;
    });
    const links = Object.values(linkMap);

    // Clear previous SVG
    d3.select(svgRef.current).selectAll('*').remove();

    // SVG setup
    const width = 800;
    const height = 600;
    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    // Color scale (hodline.com inspired palette)
    const colorScale = d3
      .scaleOrdinal()
      .domain(nodes.map((n) => n.id))
      .range([
        '#6366f1', // indigo
        '#8b5cf6', // purple
        '#ec4899', // pink
        '#f59e0b', // amber
        '#10b981', // green
        '#3b82f6', // blue
        '#ef4444', // red
        '#06b6d4', // cyan
      ]);

    // Force simulation
    const simulation = d3
      .forceSimulation(nodes)
      .force(
        'link',
        d3
          .forceLink(links)
          .id((d) => d.id)
          .distance(100)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(0, 0))
      .force('collision', d3.forceCollide().radius(50));

    // Draw links
    const link = svg
      .append('g')
      .selectAll('line')
      .data(links)
      .join('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-opacity', 0.3)
      .attr('stroke-width', (d) => Math.sqrt(d.value) * 0.5);

    // Draw nodes
    const node = svg
      .append('g')
      .selectAll('circle')
      .data(nodes)
      .join('circle')
      .attr('r', (d) => Math.sqrt(d.value) * 5 + 10)
      .attr('fill', (d) => colorScale(d.id))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('stroke-width', 4).attr('opacity', 1);
        setSelectedEmotion(d);
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke-width', 2).attr('opacity', 0.9);
      })
      .call(
        d3
          .drag()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended)
      );

    // Add labels
    const labels = svg
      .append('g')
      .selectAll('text')
      .data(nodes)
      .join('text')
      .text((d) => d.id)
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .attr('fill', '#1e293b')
      .attr('text-anchor', 'middle')
      .attr('dy', '.35em')
      .style('pointer-events', 'none');

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => d.source.x)
        .attr('y1', (d) => d.source.y)
        .attr('x2', (d) => d.target.x)
        .attr('y2', (d) => d.target.y);

      node.attr('cx', (d) => d.x).attr('cy', (d) => d.y);

      labels.attr('x', (d) => d.x).attr('y', (d) => d.y);
    });

    // Drag functions
    function dragstarted(event, d) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      d.fx = d.x;
      d.fy = d.y;
    }

    function dragged(event, d) {
      d.fx = event.x;
      d.fy = event.y;
    }

    function dragended(event, d) {
      if (!event.active) simulation.alphaTarget(0);
      d.fx = null;
      d.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [analyses]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Emotion Network Graph</CardTitle>
        <p className="text-sm text-gray-600">
          Interactive visualization of emotion relationships. Drag nodes to explore connections.
        </p>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          <svg ref={svgRef} className="w-full"></svg>
          {selectedEmotion && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg w-full">
              <h4 className="font-bold text-lg">{selectedEmotion.id}</h4>
              <p className="text-sm text-gray-600">Occurrences: {selectedEmotion.value}</p>
              <p className="text-sm text-gray-600">
                Avg Score: {selectedEmotion.avgScore.toFixed(2)}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Emotion Bubble Chart - Shows intensity and frequency
 */
export const EmotionBubbleChart = ({ analyses }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!analyses || analyses.length === 0 || !svgRef.current) return;

    // Aggregate emotion data
    const emotionStats = {};
    analyses.forEach((analysis) => {
      const emotions = analysis.emotions || {};
      Object.entries(emotions).forEach(([emotion, score]) => {
        if (!emotionStats[emotion]) {
          emotionStats[emotion] = { name: emotion, count: 0, totalScore: 0, scores: [] };
        }
        emotionStats[emotion].count += 1;
        emotionStats[emotion].totalScore += score;
        emotionStats[emotion].scores.push(score);
      });
    });

    const bubbleData = Object.values(emotionStats).map((stat) => ({
      name: stat.name,
      frequency: stat.count,
      avgIntensity: stat.totalScore / stat.count,
      maxIntensity: Math.max(...stat.scores),
    }));

    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const width = 800;
    const height = 500;
    const margin = { top: 40, right: 100, bottom: 60, left: 60 };

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Scales
    const xScale = d3
      .scaleLinear()
      .domain([0, d3.max(bubbleData, (d) => d.frequency) * 1.1])
      .range([0, chartWidth]);

    const yScale = d3
      .scaleLinear()
      .domain([0, 100])
      .range([chartHeight, 0]);

    const sizeScale = d3
      .scaleSqrt()
      .domain([0, 100])
      .range([5, 40]);

    const colorScale = d3
      .scaleSequential()
      .domain([0, 100])
      .interpolator(d3.interpolateRgbBasis(['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b']));

    // Axes
    svg
      .append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .append('text')
      .attr('x', chartWidth / 2)
      .attr('y', 40)
      .attr('fill', '#1e293b')
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .text('Frequency (# of occurrences)');

    svg
      .append('g')
      .call(d3.axisLeft(yScale))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -chartHeight / 2)
      .attr('y', -40)
      .attr('fill', '#1e293b')
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .attr('text-anchor', 'middle')
      .text('Average Intensity');

    // Tooltip
    const tooltip = d3
      .select('body')
      .append('div')
      .style('position', 'absolute')
      .style('background', 'rgba(0, 0, 0, 0.8)')
      .style('color', '#fff')
      .style('padding', '8px 12px')
      .style('border-radius', '6px')
      .style('font-size', '12px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    // Bubbles
    svg
      .selectAll('circle')
      .data(bubbleData)
      .join('circle')
      .attr('cx', (d) => xScale(d.frequency))
      .attr('cy', (d) => yScale(d.avgIntensity))
      .attr('r', (d) => sizeScale(d.maxIntensity))
      .attr('fill', (d) => colorScale(d.avgIntensity))
      .attr('opacity', 0.7)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).attr('opacity', 1).attr('stroke-width', 3);
        tooltip
          .style('opacity', 1)
          .html(
            `<strong>${d.name}</strong><br/>Frequency: ${d.frequency}<br/>Avg Intensity: ${d.avgIntensity.toFixed(
              1
            )}<br/>Max Intensity: ${d.maxIntensity.toFixed(1)}`
          )
          .style('left', event.pageX + 10 + 'px')
          .style('top', event.pageY - 30 + 'px');
      })
      .on('mouseout', function () {
        d3.select(this).attr('opacity', 0.7).attr('stroke-width', 2);
        tooltip.style('opacity', 0);
      });

    // Labels
    svg
      .selectAll('text.label')
      .data(bubbleData)
      .join('text')
      .attr('class', 'label')
      .attr('x', (d) => xScale(d.frequency))
      .attr('y', (d) => yScale(d.avgIntensity))
      .attr('text-anchor', 'middle')
      .attr('dy', '.35em')
      .attr('font-size', 11)
      .attr('font-weight', 'bold')
      .attr('fill', '#fff')
      .style('pointer-events', 'none')
      .text((d) => (d.frequency > 5 ? d.name : ''));

    return () => {
      tooltip.remove();
    };
  }, [analyses]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Emotion Intensity & Frequency</CardTitle>
        <p className="text-sm text-gray-600">
          Bubble size represents maximum intensity. Hover to see details.
        </p>
      </CardHeader>
      <CardContent>
        <svg ref={svgRef} className="w-full"></svg>
      </CardContent>
    </Card>
  );
};

/**
 * Emotion Timeline - Shows trends over time
 */
export const EmotionTimeline = ({ analyses }) => {
  const svgRef = useRef(null);
  const [timeRange, setTimeRange] = useState('30d');

  useEffect(() => {
    if (!analyses || analyses.length === 0 || !svgRef.current) return;

    // Parse timestamps and group by date
    const parseTimestamp = (ts) => {
      try {
        return new Date(ts);
      } catch {
        return null;
      }
    };

    const validAnalyses = analyses
      .filter((a) => a.timestamp)
      .map((a) => ({ ...a, date: parseTimestamp(a.timestamp) }))
      .filter((a) => a.date)
      .sort((a, b) => a.date - b.date);

    if (validAnalyses.length === 0) return;

    // Filter by time range
    const now = new Date();
    const daysBack = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    const filteredAnalyses = validAnalyses.filter((a) => a.date >= cutoffDate);

    // Group by day and emotion
    const dateEmotionMap = {};
    filteredAnalyses.forEach((a) => {
      const dateKey = a.date.toISOString().split('T')[0];
      if (!dateEmotionMap[dateKey]) {
        dateEmotionMap[dateKey] = {};
      }
      const emotions = a.emotions || {};
      Object.entries(emotions).forEach(([emotion, score]) => {
        if (!dateEmotionMap[dateKey][emotion]) {
          dateEmotionMap[dateKey][emotion] = [];
        }
        dateEmotionMap[dateKey][emotion].push(score);
      });
    });

    // Calculate averages per day
    const timelineData = [];
    Object.entries(dateEmotionMap).forEach(([date, emotions]) => {
      const dataPoint = { date: new Date(date) };
      Object.entries(emotions).forEach(([emotion, scores]) => {
        dataPoint[emotion] = scores.reduce((a, b) => a + b, 0) / scores.length;
      });
      timelineData.push(dataPoint);
    });

    timelineData.sort((a, b) => a.date - b.date);

    // Get top 5 emotions
    const emotionTotals = {};
    timelineData.forEach((d) => {
      Object.entries(d).forEach(([key, value]) => {
        if (key !== 'date') {
          emotionTotals[key] = (emotionTotals[key] || 0) + value;
        }
      });
    });
    const topEmotions = Object.entries(emotionTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([emotion]) => emotion);

    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const width = 1000;
    const height = 400;
    const margin = { top: 20, right: 150, bottom: 60, left: 60 };

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Scales
    const xScale = d3
      .scaleTime()
      .domain(d3.extent(timelineData, (d) => d.date))
      .range([0, chartWidth]);

    const yScale = d3.scaleLinear().domain([0, 100]).range([chartHeight, 0]);

    const colorScale = d3
      .scaleOrdinal()
      .domain(topEmotions)
      .range(['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981']);

    // Axes
    svg
      .append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale).ticks(8))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end');

    svg.append('g').call(d3.axisLeft(yScale));

    // Line generator
    const line = d3
      .line()
      .defined((d) => d[1] !== undefined)
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]))
      .curve(d3.curveMonotoneX);

    // Draw lines for each emotion
    topEmotions.forEach((emotion) => {
      const emotionData = timelineData.map((d) => [d.date, d[emotion]]);

      svg
        .append('path')
        .datum(emotionData)
        .attr('fill', 'none')
        .attr('stroke', colorScale(emotion))
        .attr('stroke-width', 2.5)
        .attr('d', line);

      // Add dots
      svg
        .selectAll(`.dot-${emotion}`)
        .data(emotionData.filter((d) => d[1] !== undefined))
        .join('circle')
        .attr('class', `dot-${emotion}`)
        .attr('cx', (d) => xScale(d[0]))
        .attr('cy', (d) => yScale(d[1]))
        .attr('r', 4)
        .attr('fill', colorScale(emotion))
        .attr('stroke', '#fff')
        .attr('stroke-width', 2)
        .style('cursor', 'pointer')
        .on('mouseover', function (event, d) {
          d3.select(this).attr('r', 6);
        })
        .on('mouseout', function () {
          d3.select(this).attr('r', 4);
        });
    });

    // Legend
    const legend = svg
      .append('g')
      .attr('transform', `translate(${chartWidth + 20}, 0)`);

    topEmotions.forEach((emotion, i) => {
      const legendRow = legend.append('g').attr('transform', `translate(0, ${i * 25})`);

      legendRow
        .append('rect')
        .attr('width', 15)
        .attr('height', 15)
        .attr('fill', colorScale(emotion));

      legendRow
        .append('text')
        .attr('x', 20)
        .attr('y', 12)
        .attr('font-size', 12)
        .attr('fill', '#1e293b')
        .text(emotion);
    });
  }, [analyses, timeRange]);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>Emotion Trends Over Time</CardTitle>
            <p className="text-sm text-gray-600">Top 5 emotions tracked over time</p>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={timeRange === '7d' ? 'default' : 'outline'}
              onClick={() => setTimeRange('7d')}
            >
              7 Days
            </Button>
            <Button
              size="sm"
              variant={timeRange === '30d' ? 'default' : 'outline'}
              onClick={() => setTimeRange('30d')}
            >
              30 Days
            </Button>
            <Button
              size="sm"
              variant={timeRange === '90d' ? 'default' : 'outline'}
              onClick={() => setTimeRange('90d')}
            >
              90 Days
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <svg ref={svgRef} className="w-full"></svg>
      </CardContent>
    </Card>
  );
};

/**
 * Emotion Distribution Bar Chart - Simple summary
 */
export const EmotionDistribution = ({ analyses }) => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!analyses || analyses.length === 0 || !svgRef.current) return;

    // Count dominant emotions
    const emotionCounts = {};
    analyses.forEach((a) => {
      const dominant = a.dominant_emotion;
      if (dominant) {
        emotionCounts[dominant] = (emotionCounts[dominant] || 0) + 1;
      }
    });

    const data = Object.entries(emotionCounts)
      .map(([emotion, count]) => ({ emotion, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Clear previous
    d3.select(svgRef.current).selectAll('*').remove();

    const width = 600;
    const height = 400;
    const margin = { top: 20, right: 30, bottom: 80, left: 60 };

    const svg = d3
      .select(svgRef.current)
      .attr('width', '100%')
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Scales
    const xScale = d3
      .scaleBand()
      .domain(data.map((d) => d.emotion))
      .range([0, chartWidth])
      .padding(0.2);

    const yScale = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.count)])
      .range([chartHeight, 0]);

    const colorScale = d3
      .scaleSequential()
      .domain([0, data.length - 1])
      .interpolator(d3.interpolateRgbBasis(['#6366f1', '#8b5cf6', '#ec4899']));

    // Axes
    svg
      .append('g')
      .attr('transform', `translate(0, ${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .attr('transform', 'rotate(-45)')
      .style('text-anchor', 'end')
      .attr('font-weight', 'bold');

    svg.append('g').call(d3.axisLeft(yScale));

    // Bars
    svg
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', (d) => xScale(d.emotion))
      .attr('y', (d) => yScale(d.count))
      .attr('width', xScale.bandwidth())
      .attr('height', (d) => chartHeight - yScale(d.count))
      .attr('fill', (d, i) => colorScale(i))
      .attr('rx', 4)
      .style('cursor', 'pointer')
      .on('mouseover', function () {
        d3.select(this).attr('opacity', 0.8);
      })
      .on('mouseout', function () {
        d3.select(this).attr('opacity', 1);
      });

    // Value labels
    svg
      .selectAll('text.value')
      .data(data)
      .join('text')
      .attr('class', 'value')
      .attr('x', (d) => xScale(d.emotion) + xScale.bandwidth() / 2)
      .attr('y', (d) => yScale(d.count) - 5)
      .attr('text-anchor', 'middle')
      .attr('font-size', 12)
      .attr('font-weight', 'bold')
      .attr('fill', '#1e293b')
      .text((d) => d.count);
  }, [analyses]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dominant Emotion Distribution</CardTitle>
        <p className="text-sm text-gray-600">Top 10 most frequent dominant emotions</p>
      </CardHeader>
      <CardContent>
        <svg ref={svgRef} className="w-full"></svg>
      </CardContent>
    </Card>
  );
};
